import { useLazyQuery } from '@apollo/client/react';
import { ControlledTextField } from '@drycut/controlled-components';
import { SearchCustomersDocument } from '@drycut/graphql';
import { ActionButtons, Button, Combobox, Para, Select, Stack } from '@drycut/ui-components';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { COUPON_TYPE_OPTIONS, type CouponFormData, type SelectOption } from './types';

type Customer = {
  id: string;
  name: string;
  phoneNumber: string;
};

type Props = {
  customer: Customer | null;
  error?: null | string;
  loading?: boolean;
  onCancel: () => void;
  onCustomerChange: (customer: Customer | null) => void;
  onSubmit: (data: CouponFormData) => void;
  onTypeChange: (option: SelectOption) => void;
  reason?: string;
  selectedType: SelectOption | undefined;
};

export function CouponDetailsStep({
  onSubmit,
  onCancel,
  loading,
  selectedType,
  onTypeChange,
  customer,
  onCustomerChange,
  reason = '',
  error,
}: Props): React.ReactElement {
  const [searchCustomers, { data: customerData, loading: searchLoading }] =
    useLazyQuery(SearchCustomersDocument);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const formMethods = useForm<CouponFormData>({
    defaultValues: {
      reason,
    },
  });

  const handleCustomerSearch = (query: string) => {
    clearTimeout(debounceRef.current);
    if (query.length < 2) {
      return;
    }
    debounceRef.current = setTimeout(() => {
      searchCustomers({ variables: { query } });
    }, 300);
  };

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const isGiftCard = selectedType?.value === 'Coupons::Goodwills::GiftCard';

  const handleSubmit = (data: CouponFormData) => {
    if (!customer && !isGiftCard) return;
    onSubmit(data);
  };

  return (
    <Stack
      as="form"
      gap={4}
      className="px-10"
      noValidate
      onSubmit={formMethods.handleSubmit(handleSubmit)}
    >
      <FormProvider {...formMethods}>
        <Stack as="fieldset" gap={4} disabled={loading}>
          <Select
            name="couponType"
            options={COUPON_TYPE_OPTIONS}
            label="Typ des Gutscheins"
            selected={selectedType}
            onChange={onTypeChange}
            disabled={loading}
          />

          <Combobox
            name="customer"
            label="Kunde"
            placeholder="Tel. DRYCUT Kunde"
            items={customerData?.searchCustomers || []}
            selected={customer}
            onChange={onCustomerChange}
            onQueryChange={handleCustomerSearch}
            displayValue={(c) => (c ? `${c.name} (${c.phoneNumber})` : '')}
            renderItem={(c) => (
              <div className="flex flex-col">
                <span className="block truncate font-normal group-data-[selected]:font-semibold">
                  {c.name}
                </span>
                <span className="block truncate text-xs text-gray-500 group-data-[focus]:text-white/80">
                  {c.phoneNumber}
                </span>
              </div>
            )}
            loading={searchLoading}
            disabled={loading}
            emptyMessage="Keine Kunden gefunden"
          />

          <ControlledTextField name="reason" label="Notitz" />
        </Stack>

        {error && <Para className="text-red-600 font-bold">{error}</Para>}

        <ActionButtons>
          <Button
            type="submit"
            loading={loading}
            disabled={!selectedType || (!customer && !isGiftCard)}
          >
            Bestätigen
          </Button>

          <Button type="button" fill="outlined" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        </ActionButtons>
      </FormProvider>
    </Stack>
  );
}
